import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../i18n";
import ThemeSwitcher from "../ThemeSwitcher";
import NotificationsBell from "../NotificationsBell";
import SearchBar from "../SearchBar";
import UserMenu from "../UserMenu";
import GoogleTranslate from "../GoogleTranslate";
import WalletPopover from "../WalletPopover";
import GuestBonusModal from "../GuestBonusModal";
import api from "../../lib/api";
import {
  Crown, Shield, Home as HomeIcon, MessageSquare, Trophy, Users,
  LayoutDashboard, Circle, Mail, Menu, X, Settings,
  Target, Crosshair, Map, BookOpen, Server, Megaphone, Hand, Coffee,
  Cpu, Calendar, Video, Award, List, Star, Zap, Globe, Code,
  ChevronRight, Gamepad2, Info, Bell, Link as LinkIcon, Hash,
  BarChart2, ShieldCheck, User, Package, ShoppingCart,
  Facebook, Twitter, Youtube, Instagram, Twitch, Newspaper, Flame, Swords, Download, HardDrive,
  Heart, Sparkles, Gift, Coins, ExternalLink, LogIn, UserPlus
} from "lucide-react";

const FALLBACK_NAV = (t) => [
  { url: "/", label: t("nav.home") || "Acasă", icon: "home" },
  { url: "/forum", label: t("nav.forums") || "Forum", icon: "message-square" },
  { url: "/turnee", label: "Turnee", icon: "trophy" },
  { url: "/servere", label: "Servere", icon: "server" },
  { url: "/clans", label: "Clanuri", icon: "shield" },
  { url: "/resurse", label: "Resurse", icon: "download" },
  { url: "/members", label: t("nav.members") || "Membri", icon: "users" },
  { url: "/vip", label: "VIP", icon: "sparkles" },
  { url: "/donate", label: t("nav.donate") || "Donează", icon: "heart" },
];

const ICON_MAP = {
  home: HomeIcon,
  "message-square": MessageSquare,
  users: Users,
  trophy: Trophy,
  crown: Crown,
  shield: Shield,
  "layout-dashboard": LayoutDashboard,
  settings: Settings,
  target: Target,
  crosshair: Crosshair,
  map: Map,
  book: BookOpen,
  server: Server,
  megaphone: Megaphone,
  newspaper: Newspaper,
  flame: Flame,
  swords: Swords,
  download: Download,
  "hard-drive": HardDrive,
  hand: Hand,
  coffee: Coffee,
  cpu: Cpu,
  calendar: Calendar,
  video: Video,
  award: Award,
  list: List,
  star: Star,
  zap: Zap,
  globe: Globe,
  code: Code,
  "chevron-right": ChevronRight,
  gamepad: Gamepad2,
  gamepad2: Gamepad2,
  info: Info,
  bell: Bell,
  link: LinkIcon,
  hash: Hash,
  "bar-chart": BarChart2,
  "bar-chart-2": BarChart2,
  "shield-check": ShieldCheck,
  user: User,
  package: Package,
  cart: ShoppingCart,
  "shopping-cart": ShoppingCart,
  mail: Mail,
  heart: Heart,
  sparkles: Sparkles,
  gift: Gift,
  coins: Coins,
};

function getIconComponent(name) {
  if (!name) return Circle;
  const key = name.toLowerCase().replace(/[_\s]+/g, "-");
  return ICON_MAP[key] || Circle;
}

function visibleToCurrentUser(item, user) {
  const vt = item.visible_to || ["all"];
  if (vt.includes("all")) return true;
  if (!user) return vt.includes("guest");
  if (vt.includes("user")) return true;
  if (user.is_vip && vt.includes("vip")) return true;
  if ((user.role === "admin" || user.role === "root") && vt.includes("admin")) return true;
  return false;
}

export default function Header() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [online, setOnline] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerMenu, setHeaderMenu] = useState(null);
  const [appearance, setAppearance] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const hiddenAt = localStorage.getItem("bonus_modal_hidden");
    if (!hiddenAt || Date.now() - parseInt(hiddenAt, 10) > 6 * 3600 * 1000) {
      const t = setTimeout(() => setShowBonusModal(true), 2500);
      return () => clearTimeout(t);
    }
  }, []);

  const NAV = useMemo(() => {
    if (!headerMenu || !headerMenu.items?.length) return FALLBACK_NAV(t);
    return headerMenu.items
      .filter((item) => visibleToCurrentUser(item, user))
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => ({ url: item.url, label: item.label, icon: item.icon, target: item.target }));
  }, [headerMenu, user, t]);

  const allLinks = useMemo(() => {
    return NAV;
  }, [NAV]);

  const { leftLinks, rightLinks } = useMemo(() => {
    const mid = Math.ceil(allLinks.length / 2);
    return {
      leftLinks: allLinks.slice(0, mid),
      rightLinks: allLinks.slice(mid),
    };
  }, [allLinks]);

  useEffect(() => {
    const fetch = () => api.get("/stats").then((r) => setOnline(r.data.online_now || 0)).catch(() => {});
    fetch();
    const t2 = setInterval(fetch, 30000);
    return () => clearInterval(t2);
  }, []);

  useEffect(() => {
    const fetchAppearance = () =>
      api.get("/idg/appearance").then((r) => setAppearance(r.data)).catch(() => {});
    const fetchMenu = () => 
      api.get("/idg/menus/public/header").then((r) => setHeaderMenu(r.data)).catch(() => {});
    
    fetchAppearance();
    fetchMenu();
    window.addEventListener("idg:appearance-updated", fetchAppearance);
    window.addEventListener("idg:menu-updated", fetchMenu);
    window.addEventListener("focus", fetchMenu);
    
    let bc;
    if (window.BroadcastChannel) {
      bc = new BroadcastChannel("idg_sync");
      bc.onmessage = (event) => {
        if (event.data === "appearance-updated") {
          fetchAppearance();
        }
        if (event.data === "menu-updated") {
          fetchMenu();
        }
      };
    }

    return () => {
      window.removeEventListener("idg:appearance-updated", fetchAppearance);
      window.removeEventListener("idg:menu-updated", fetchMenu);
      window.removeEventListener("focus", fetchMenu);
      if (bc) bc.close();
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-[#080a0f] border-b border-white/[0.08] backdrop-blur-xl font-sans select-none">
      {/* Laser Glow Keyframes & Custom Micro-animations */}
      <style>{`
        @keyframes tf-laser-sweep {
          0% { transform: translateX(-100%); opacity: 0; }
          50% { opacity: 0.8; }
          100% { transform: translateX(200%); opacity: 0; }
        }
        @keyframes tf-pulse-glow {
          0%, 100% { filter: drop-shadow(0 0 4px rgba(255, 79, 0, 0.4)); }
          50% { filter: drop-shadow(0 0 10px rgba(255, 79, 0, 0.8)); }
        }
        @keyframes tf-gold-glow {
          0%, 100% { filter: drop-shadow(0 0 4px rgba(251, 191, 36, 0.4)); }
          50% { filter: drop-shadow(0 0 10px rgba(251, 191, 36, 0.8)); }
        }
        @keyframes tf-heart-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        .tf-nav-item:hover .tf-laser-indicator {
          transform: scaleX(1);
        }
      `}</style>

      {/* Desktop Layout */}
      <div className="hidden md:flex items-stretch h-24 max-w-[1500px] mx-auto relative select-none">
        
        {/* Left Side Navigation Block */}
        <div className="flex-1 flex flex-col justify-between py-2.5 pr-8 relative z-10 mr-[-20px]">
          {/* Decorative background with clip-path */}
          <div 
            className="absolute inset-0 bg-gradient-to-r from-[#0a0c13] via-[#0e1017] to-[#12151e] -z-10 pointer-events-none border-b border-white/[0.04]"
            style={{
              clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 100%, 0 100%)',
            }}
          />

          {/* Top Row: Follow Us & Social Icons & Left Wallet */}
          <div className="flex items-center justify-between gap-4 h-8 px-4">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase font-chakra font-black tracking-widest text-zinc-500">
                {t("header.follow_us") || "URMĂREȘTE-NE:"}
              </span>
              <div className="flex items-center gap-2">
                <a 
                  href={appearance?.header_social_facebook || "https://facebook.com"} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="w-5 h-5 flex items-center justify-center rounded-sm bg-white/[0.03] border border-white/5 text-zinc-400 hover:text-[#1877F2] hover:bg-[#1877F2]/10 hover:border-[#1877F2]/30 hover:scale-110 hover:-translate-y-0.5 transition-all duration-200"
                  title="Facebook"
                >
                  <Facebook className="w-3 h-3" />
                </a>
                <a 
                  href={appearance?.header_social_twitter || "https://twitter.com"} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="w-5 h-5 flex items-center justify-center rounded-sm bg-white/[0.03] border border-white/5 text-zinc-400 hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10 hover:border-[#1DA1F2]/30 hover:scale-110 hover:-translate-y-0.5 transition-all duration-200"
                  title="Twitter / X"
                >
                  <Twitter className="w-3 h-3" />
                </a>
                <a 
                  href={appearance?.header_social_youtube || "https://youtube.com"} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="w-5 h-5 flex items-center justify-center rounded-sm bg-white/[0.03] border border-white/5 text-zinc-400 hover:text-[#FF0000] hover:bg-[#FF0000]/10 hover:border-[#FF0000]/30 hover:scale-110 hover:-translate-y-0.5 transition-all duration-200"
                  title="YouTube"
                >
                  <Youtube className="w-3 h-3" />
                </a>
                <a 
                  href={appearance?.header_social_instagram || "https://instagram.com"} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="w-5 h-5 flex items-center justify-center rounded-sm bg-white/[0.03] border border-white/5 text-zinc-400 hover:text-[#E4405F] hover:bg-[#E4405F]/10 hover:border-[#E4405F]/30 hover:scale-110 hover:-translate-y-0.5 transition-all duration-200"
                  title="Instagram"
                >
                  <Instagram className="w-3 h-3" />
                </a>
                <a 
                  href={appearance?.header_social_twitch || "https://twitch.tv"} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="w-5 h-5 flex items-center justify-center rounded-sm bg-white/[0.03] border border-white/5 text-zinc-400 hover:text-[#9146FF] hover:bg-[#9146FF]/10 hover:border-[#9146FF]/30 hover:scale-110 hover:-translate-y-0.5 transition-all duration-200"
                  title="Twitch"
                >
                  <Twitch className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Wallet Button in the Left Section */}
            {user && (
              <div className="flex items-center pr-3">
                <WalletPopover align="left" />
              </div>
            )}
          </div>
          
          {/* Bottom Row: Navigation Links (Left Group) */}
          <div className="flex items-center justify-end gap-1 h-10 pr-2">
            {leftLinks.map((link) => (
              <CustomNavLink 
                key={link.url} 
                {...link} 
                isActive={location.pathname === link.url || (link.url !== "/" && location.pathname.startsWith(link.url))} 
              />
            ))}
          </div>
        </div>

        {/* Center Logo Section */}
        {(() => {
          const logoWidth = appearance?.header_logo_width || 176;
          return (
          <div 
            className="flex-shrink-0 relative flex items-center justify-center bg-gradient-to-b from-[#14161f] to-[#0a0c12] overflow-hidden group/center shadow-2xl"
            style={{
              width: logoWidth,
              clipPath: 'polygon(0 0, 100% 0, calc(100% - 20px) 100%, 20px 100%)'
            }}
          >
            {/* SVG Background with slants & borders */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${logoWidth} 96`} preserveAspectRatio="none">
              <defs>
                <linearGradient id="logo-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#1a1c26" />
                  <stop offset="100%" stopColor="#08090d" />
                </linearGradient>
              </defs>
              <path d={`M 0 0 L ${logoWidth} 0 L ${logoWidth - 20} 96 L 20 96 Z`} fill="url(#logo-grad)" />
              <line x1="0" y1="0" x2="20" y2="96" stroke="var(--tf-primary)" strokeOpacity="0.5" strokeWidth="1.5" />
              <line x1={logoWidth} y1="0" x2={logoWidth - 20} y2="96" stroke="var(--tf-primary)" strokeOpacity="0.5" strokeWidth="1.5" />
            </svg>

            {/* Glowing Accent Top Bar */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--tf-primary)] to-transparent opacity-70" />

            <Link to="/" className="relative z-10 hover:scale-105 transition-all duration-300 flex items-center justify-center py-2" data-testid="logo-link">
              {appearance?.logo_dark_url || appearance?.logo_url ? (
                <img 
                  src={appearance.logo_dark_url || appearance.logo_url} 
                  alt="Logo" 
                  className="w-auto object-contain drop-shadow-[0_0_15px_rgba(255,79,0,0.4)]" 
                  style={{ height: `${appearance?.header_logo_height || 72}px`, maxWidth: `${logoWidth - 30}px` }}
                />
              ) : (
                <>
                  {(appearance?.header_logo_type || "geometric") === "geometric" && <PlayerXLogo />}
                  {appearance?.header_logo_type === "text" && (
                    <span className="tf-heading font-black text-[var(--tf-primary)] text-xl tracking-widest filter drop-shadow-[0_0_12px_var(--tf-glow)] notranslate" translate="no">
                      {appearance?.header_logo_text || "INDUNGI"}
                    </span>
                  )}
                </>
              )}
            </Link>
          </div>
          );
        })()}

        {/* Right Side Navigation Block */}
        <div className="flex-1 flex flex-col justify-between py-2.5 pl-8 relative z-10 ml-[-20px]">
          {/* Decorative background with clip-path */}
          <div 
            className="absolute inset-0 bg-gradient-to-r from-[#12151e] via-[#0e1017] to-[#0a0c13] -z-10 pointer-events-none border-b border-white/[0.04]"
            style={{
              clipPath: 'polygon(20px 0, 100% 0, 100% 100%, 0 100%)',
            }}
          />

          {/* Top Row: User Utilities & Actions */}
          <div className="flex items-center justify-end h-8 w-full pr-4">
            <div className="flex items-center gap-2 ml-auto">
              <SearchBar collapsible />
              <GoogleTranslate id="google_translate_element_desktop" />
              
              {/* Radar-Style Live Online Counter */}
              <div 
                className="hidden lg:flex items-center gap-2 h-8 px-3 bg-white/[0.03] border border-white/10 rounded-[4px]" 
                data-testid="online-counter" 
                translate="no"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 shadow-[0_0_8px_#10b981]"></span>
                </span>
                <span className="font-mono text-[11px] font-bold text-white tracking-wider">{online}</span>
                <span className="text-[9px] font-chakra font-black tracking-widest text-zinc-500 uppercase">{t("header.online") || "ONLINE"}</span>
              </div>

              <ThemeSwitcher />

              {/* GameCMS Neo Gift / Daily Reward Trigger */}
              <button
                id="giftBtn"
                onClick={() => setShowBonusModal(true)}
                className="gift relative w-8 h-8 rounded-[4px] flex items-center justify-center bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 hover:border-amber-400 text-amber-400 hover:text-amber-300 transition-all duration-200 hover:scale-105 shadow-[0_0_12px_rgba(245,158,11,0.2)]"
                title="Cadou Zilnic & Recompense"
              >
                <Gift className="w-3.5 h-3.5" />
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 animate-ping" />
              </button>

              {user && <NotificationsBell />}
              {user && (
                <Link 
                  to="/messages" 
                  className="w-8 h-8 rounded-[4px] flex items-center justify-center bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[var(--tf-primary)]/50 text-zinc-400 hover:text-white transition-all duration-200 hover:scale-105" 
                  title="Mesaje" 
                  data-testid="nav-messages"
                >
                  <Mail className="w-3.5 h-3.5" />
                </Link>
              )}

              {user ? (
                <UserMenu />
              ) : (
                <div className="flex items-center gap-2 pl-1">
                  <Link 
                    to="/login" 
                    className="flex items-center gap-1.5 px-3 h-8 text-[11px] font-chakra font-bold uppercase tracking-wider text-zinc-300 hover:text-white bg-white/[0.03] hover:bg-white/10 border border-white/10 rounded-[4px] transition-all" 
                    data-testid="header-login"
                  >
                    <LogIn className="w-3 h-3 text-zinc-400" />
                    <span>{t("nav.login") || "LOGIN"}</span>
                  </Link>

                  <Link 
                    to="/register" 
                    className="flex items-center gap-1.5 px-3.5 h-8 text-[11px] font-chakra font-black uppercase tracking-wider text-black bg-[var(--tf-primary)] hover:bg-white hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] rounded-[4px] transition-all shadow-[0_0_10px_var(--tf-glow)]" 
                    data-testid="header-register"
                  >
                    <UserPlus className="w-3 h-3 text-black" />
                    <span>{t("nav.register") || "ÎNREGISTRARE"}</span>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Row: Navigation Links (Right Group) */}
          <div className="flex items-center justify-start gap-1 h-10 pl-2">
            {rightLinks.map((link) => (
              <CustomNavLink 
                key={link.url} 
                {...link} 
                isActive={location.pathname === link.url || (link.url !== "/" && location.pathname.startsWith(link.url))} 
              />
            ))}
          </div>
        </div>

      </div>

      {/* Mobile Layout */}
      <div className="md:hidden flex items-center justify-between h-16 px-4 bg-[#0a0c13] border-b border-white/[0.08]">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          {appearance?.logo_dark_url || appearance?.logo_url ? (
            <img src={appearance.logo_dark_url || appearance.logo_url} alt="Logo" className="h-8 w-auto object-contain" />
          ) : (
            <>
              {(appearance?.header_logo_type || "geometric") === "geometric" && (
                <div className="w-8 h-8 flex items-center justify-center scale-75">
                  <PlayerXLogo />
                </div>
              )}
              {appearance?.header_logo_type === "text" && (
                <span className="tf-heading font-black text-[var(--tf-primary)] text-base tracking-wider notranslate" translate="no">
                  {appearance?.header_logo_text || "INDUNGI"}
                </span>
              )}
            </>
          )}
        </Link>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowBonusModal(true)}
            className="w-9 h-9 rounded-[6px] flex items-center justify-center bg-amber-500/10 border border-amber-500/30 text-amber-400 transition-transform active:scale-95 shadow-sm"
            title="Cadou"
          >
            <Gift className="w-4.5 h-4.5" />
          </button>
          {user && <WalletPopover />}
          {user && <NotificationsBell />}
          {user ? (
            <UserMenu />
          ) : (
            <Link to="/login" className="px-3 py-1.5 text-xs font-chakra font-bold text-white bg-white/10 rounded-sm transition-colors active:bg-white/20">
              LOGIN
            </Link>
          )}
          <button 
            className="p-2.5 bg-white/5 border border-white/10 text-zinc-300 hover:text-white rounded-[6px] transition-transform active:scale-95 shadow-sm" 
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0c13] border-t border-white/10 py-4 px-4 space-y-1.5 shadow-2xl">
          {allLinks.map((n) => {
            const Icon = getIconComponent(n.icon);
            const isAct = location.pathname === n.url;
            return (
              <Link 
                key={n.url} 
                to={n.url} 
                target={n.target || "_self"} 
                onClick={() => setMobileOpen(false)} 
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-sm transition-all font-chakra uppercase tracking-wider text-xs font-bold ${
                  isAct 
                    ? "bg-[var(--tf-primary)]/15 border border-[var(--tf-primary)]/40 text-white" 
                    : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-4 h-4 ${isAct ? 'text-[var(--tf-primary)]' : 'text-zinc-500'}`} />
                  <span>{n.label}</span>
                </div>
                {n.url === "/vip" && (
                  <span className="px-1.5 py-0.5 text-[9px] font-black bg-amber-500/20 text-amber-400 border border-amber-500/40">VIP</span>
                )}
                {n.url === "/donate" && (
                  <span className="px-1.5 py-0.5 text-[9px] font-black bg-rose-500/20 text-rose-400 border border-rose-500/40">HOT</span>
                )}
              </Link>
            );
          })}
          {!user && (
            <Link 
              to="/register" 
              onClick={() => setMobileOpen(false)} 
              className="flex items-center justify-center gap-2 px-3 py-2.5 mt-3 text-xs font-chakra font-black uppercase tracking-widest text-black bg-[var(--tf-primary)] rounded-sm shadow-lg"
            >
              <UserPlus className="w-4 h-4" />
              <span>{t("nav.register") || "CREEAZĂ CONT"}</span>
            </Link>
          )}
          <div className="pt-4 flex items-center justify-between border-t border-white/5">
            <SearchBar />
            <div className="flex items-center gap-2">
              <GoogleTranslate />
              <ThemeSwitcher />
            </div>
          </div>
        </div>
      )}

      {/* Guest & Daily Bonus Modal */}
      <GuestBonusModal 
        isOpen={showBonusModal} 
        onClose={() => setShowBonusModal(false)} 
      />
    </header>
  );
}

function CustomNavLink({ url, label, icon, target, isActive }) {
  const Icon = getIconComponent(icon);
  const isVip = url === "/vip";
  const isDonate = url === "/donate";

  return (
    <Link
      to={url}
      target={target || "_self"}
      className={`tf-nav-item flex items-center gap-1.5 px-4 h-full text-xs font-chakra font-black uppercase tracking-wider transition-all duration-200 relative group ${
        isActive 
          ? isVip 
            ? "text-amber-400" 
            : isDonate 
              ? "text-rose-400" 
              : "text-white" 
          : isVip 
            ? "text-amber-400/80 hover:text-amber-300" 
            : isDonate 
              ? "text-rose-400/80 hover:text-rose-300" 
              : "text-zinc-400 hover:text-white"
      }`}
    >
      {/* Soft top-down subtle glow on active or hover */}
      <div 
        className={`absolute inset-0 bg-gradient-to-b ${
          isActive 
            ? isVip 
              ? "from-amber-500/15 via-amber-500/[0.02] to-transparent opacity-100" 
              : isDonate 
                ? "from-rose-500/15 via-rose-500/[0.02] to-transparent opacity-100" 
                : "from-[var(--tf-primary)]/15 via-[var(--tf-primary)]/[0.02] to-transparent opacity-100"
            : "from-white/[0.04] to-transparent opacity-0 group-hover:opacity-100"
        } transition-opacity duration-300 pointer-events-none`} 
      />

      <Icon 
        className={`w-3.5 h-3.5 transition-all duration-200 group-hover:scale-110 group-hover:-translate-y-0.5 relative z-10 ${
          isVip 
            ? 'text-amber-400 drop-shadow-[0_0_6px_rgba(251,191,36,0.6)]' 
            : isDonate 
              ? 'text-rose-400 group-hover:animate-pulse' 
              : isActive 
                ? 'text-[var(--tf-primary)] drop-shadow-[0_0_6px_var(--tf-glow)]' 
                : 'text-zinc-500 group-hover:text-[var(--tf-primary)]'
        }`} 
      /> 
      <span className="relative z-10">{label}</span>

      {/* Razor-Sharp Glowing Laser Line - Flushed with header bottom */}
      <div 
        className={`tf-laser-indicator absolute bottom-0 left-0 right-0 h-[2.5px] transition-transform duration-300 origin-center ${
          isVip 
            ? 'bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.9)]' 
            : isDonate 
              ? 'bg-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.9)]' 
              : 'bg-[var(--tf-primary)] shadow-[0_0_10px_var(--tf-glow)]'
        } ${isActive ? 'scale-x-100' : 'scale-x-0'}`} 
      />
    </Link>
  );
}

function PlayerXLogo() {
  return (
    <svg className="w-14 h-14 text-white filter drop-shadow-[0_0_8px_var(--tf-glow)] group transition-colors duration-300 hover:text-[var(--tf-primary)]" viewBox="0 0 100 100" fill="currentColor">
      <path className="transition-transform duration-300 group-hover/center:-translate-x-1.5" d="M 40 45 L 20 25 L 20 38 L 32 50 L 20 62 L 20 75 L 40 55 Z" />
      <path className="transition-transform duration-300 group-hover/center:translate-x-1.5" d="M 60 45 L 80 25 L 80 38 L 68 50 L 80 62 L 80 75 L 60 55 Z" />
      <path className="transition-transform duration-300 group-hover/center:-translate-y-1.5" d="M 45 40 L 25 20 L 38 20 L 50 32 L 62 20 L 75 20 L 55 40 Z" />
      <path className="transition-transform duration-300 group-hover/center:translate-y-1.5" d="M 45 60 L 25 80 L 38 80 L 50 68 L 62 80 L 75 80 L 55 60 Z" />
    </svg>
  );
}
